import { getClient, ApiResponse } from '../lib/http-client';
import { ContentApiResponse, RawTranscript, PlayerTranscript } from "@/types/contentTypes";
import type { ContentSearchRequest, ContentSearchResponse } from '../types/workspaceTypes';

// Maps the raw enrichment.transcripts shape (artifactUrl = transcript.json,
// captionsUrl = the actual VTT) to what sunbird-video-player's Transcript
// interface expects - artifactUrl must be the VTT. wordByWordUrl is set to
// the same VTT for every language: the overlay's accumulation logic handles
// both word-level and sentence-level VTTs correctly on its own (a full cue is
// never truncated, only additional cues appended to a non-empty line are
// capped), so there's no need to know a given language's granularity here.
function mapRawTranscripts(raw: RawTranscript[] | undefined): PlayerTranscript[] {
  return (raw || [])
    .filter((entry): entry is RawTranscript & { captionsUrl: string } =>
      !!entry.captionsUrl && (entry.status ?? 'Live').toLowerCase() === 'live')
    .map((entry) => ({
      language: entry.language || (entry.languageCode || 'Unknown').toUpperCase(),
      identifier: entry.code,
      languageCode: entry.languageCode || '',
      artifactUrl: entry.captionsUrl,
      wordByWordUrl: entry.captionsUrl,
      sourceLanguage: !!entry.sourceLanguage,
    }));
}

const DEFAULT_CONTENT_FIELDS = [
  'transcripts', 'ageGroup', 'appIcon', 'artifactUrl', 'attributions', 'audience',
  'author', 'badgeAssertions', 'body', 'channel', 'code', 'concepts', 'contentCredits',
  'contentType', 'contributors', 'copyright', 'copyrightYear', 'createdBy', 'createdOn',
  'creator', 'creators', 'description', 'displayScore', 'domain', 'editorState',
  'flagReasons', 'flaggedBy', 'flags', 'framework', 'identifier', 'itemSetPreviewUrl',
  'keywords', 'language', 'languageCode', 'lastUpdatedOn', 'license', 'mediaType',
  'mimeType', 'name', 'originData', 'osId', 'owner', 'pkgVersion', 'publisher',
  'questions', 'resourceType', 'scoreDisplayConfig', 'status', 'streamingUrl',
  'template', 'templateId', 'totalQuestions', 'totalScore', 'versionKey', 'visibility',
  'year', 'primaryCategory', 'additionalCategories', 'interceptionPoints', 'interceptionType',
  'scoList', 'launchFile','scormVersion'
];

export class ContentService {
  public async contentSearch(
    request: ContentSearchRequest = {}
  ): Promise<ApiResponse<ContentSearchResponse>> {
    return getClient().post<ContentSearchResponse>('/composite/v1/search', {
      request: {
        filters: request.filters ?? {},
        facets: request.facets,
        limit: request.limit ?? 20,
        offset: request.offset ?? 0,
        query: request.query ?? '',
        sort_by: request.sort_by ?? { lastUpdatedOn: 'desc' },
        ...(request.fields ? { fields: request.fields } : {}),
        ...(request.exists ? { exists: request.exists } : {}),
      },
    });
  }

  public async semanticSearch(
    request: ContentSearchRequest = {}
  ): Promise<ApiResponse<ContentSearchResponse>> {
    return getClient().post<ContentSearchResponse>('/composite/v1/search', {
      request: {
        query: request.query ?? '',
        filters: request.filters ?? {},
        fields: request.fields ?? [
          'identifier', 'name', 'description', 'subject',
          'primaryCategory', 'objectType', 'mimeType', 'appIcon',
          'creator', 'lastUpdatedOn',
        ],
        limit: request.limit ?? 20,
        search_mode: 'semantic',
        semantic: { k: 50, min_score: 0.6 },
      },
    });
  }

  // `enrichTranscripts` is opt-in and defaults to false - it adds ?enrich=all,
  // which is what actually returns enrichment.transcripts ("transcripts"
  // alone is not a recognized raw field name on this endpoint). Left off by
  // default so the other existing callers of contentRead (collection/content
  // view & editor pages) keep getting exactly the response shape they always
  // have - only the video player path needs this.
  public async contentRead(
    contentId: string,
    fields?: string[],
    mode?: string,
    enrichTranscripts = false
  ): Promise<ApiResponse<ContentApiResponse>> {
    const resolvedFields = fields ?? DEFAULT_CONTENT_FIELDS;
    const params = new URLSearchParams();
    if (resolvedFields.length) params.set('fields', resolvedFields.join(','));
    if (mode) params.set('mode', mode);
    if (enrichTranscripts) params.set('enrich', 'all');
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await getClient().get<ContentApiResponse>(`/content/v1/read/${contentId}${queryString}`);
    if (enrichTranscripts && response.data?.content) {
      const mappedTranscripts = mapRawTranscripts(response.data.content.enrichment?.transcripts);
      // Only overwrite when we actually mapped something - `enrich=all` isn't guaranteed
      // to strip the raw `transcripts` field, so an empty enrichment shouldn't clobber it.
      if (mappedTranscripts.length) {
        response.data.content.transcripts = mappedTranscripts;
      }
    }
    return response;
  }

  public async contentRetire(contentIds: string[]): Promise<ApiResponse<any>> {
    return getClient().delete('/content/v1/retire', { request: { contentIds } });
  }

  public async contentPublish(
    contentId: string,
    lastPublishedBy: string
  ): Promise<ApiResponse<any>> {
    return getClient().post<any>(`/content/v1/publish/${contentId}`, {
      request: {
        content: {
          lastPublishedBy,
        },
      },
    });
  }

  public async contentReject(
    contentId: string,
    rejectReasons: string[],
    rejectComment?: string
  ): Promise<ApiResponse<any>> {
    return getClient().post<any>(`/content/v1/reject/${contentId}`, {
      request: {
        content: {
          rejectReasons: rejectReasons.length > 0 ? rejectReasons : ['Others'],
          rejectComment: rejectComment || '',
        },
      },
    });
  }

  public async contentCreate(
    name: string,
    options: {
      createdBy: string;
      creator: string;
      mimeType?: string;
      contentType?: string;
      primaryCategory?: string;
      framework?: string;
      description?: string;
      resourceType?: string;
      organisation?: string[];
      createdFor?: string[];
      targetFWIds?: string[];
      /** Dynamic fields for framework related configs */
      extraFields?: Record<string, string | string[] | number>;
    }
  ): Promise<ApiResponse<ContentCreateResponse>> {
    return getClient().post<ContentCreateResponse>('/content/v1/create', {
      request: {
        content: {
          name,
          code: crypto.randomUUID(),
          createdBy: options.createdBy,
          creator: options.creator,
          mimeType: options.mimeType ?? 'application/vnd.ekstep.ecml-archive',
          contentType: options.contentType ?? 'Resource',
          ...(options.primaryCategory ? { primaryCategory: options.primaryCategory } : {}),
          ...(options.framework && { framework: options.framework }),
          ...(options.description && { description: options.description }),
          ...(options.resourceType && { resourceType: options.resourceType }),
          ...(options.organisation?.length && { organisation: options.organisation }),
          ...(options.createdFor?.length && { createdFor: options.createdFor }),
          ...(options.targetFWIds?.length && { targetFWIds: options.targetFWIds }),
          ...options.extraFields,
        },
      },
    });
  }
}

export interface ContentCreateResponse {
  content_id: string;
  identifier: string;
  node_id: string;
  versionKey: string;
}
